/**
 * useLinkedFields — Iter217 (Smart Linked Fields)
 *
 * Provides a light-weight resolver hook wrapping the backend
 * `/api/linked-data/*` endpoints and detects field-level conflicts.
 *
 * Responsibilities:
 *  - Resolve linked entities on demand: fromProject(id), fromFormula(id),
 *    fromSalesOrder(id).
 *  - Track which fields were auto-filled ("provenance") so the UI can
 *    render badges and detect user overrides.
 *  - Non-blocking: returns { data, loading, error }. Never throws.
 *
 * Usage:
 *   const linker = useLinkedFields();
 *   const linked = await linker.fromProject("MP10482");
 *   setF(prev => ({ ...prev, ...linked, __provenance: { customer_id: "MP10482" } }));
 */
import { useCallback, useState } from "react";
import { api } from "@/lib/api";

export function useLinkedFields() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const call = useCallback(async (path) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(path);
      return res || null;
    } catch (e) {
      setError(e?.message || "Bağlantı verisi alınamadı");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fromProject = useCallback((projectKey) => {
    if (!projectKey) return Promise.resolve(null);
    return call(`/linked-data/project/${encodeURIComponent(projectKey)}`);
  }, [call]);

  const fromFormula = useCallback((formulaId) => {
    if (!formulaId) return Promise.resolve(null);
    return call(`/linked-data/formula/${encodeURIComponent(formulaId)}`);
  }, [call]);

  const fromSalesOrder = useCallback((soId) => {
    if (!soId) return Promise.resolve(null);
    return call(`/linked-data/sales-order/${encodeURIComponent(soId)}`);
  }, [call]);

  const projectsForCustomer = useCallback((customerId) => {
    if (!customerId) return Promise.resolve([]);
    return call(`/linked-data/customer/${encodeURIComponent(customerId)}/projects`);
  }, [call]);

  return { fromProject, fromFormula, fromSalesOrder, projectsForCustomer, loading, error };
}

/**
 * detectConflicts(current, linked, keys)
 *   Compare a form snapshot against a linked-data payload.
 *   Returns [{field, current, expected}] for fields where the user-visible
 *   ID differs from the linked authoritative one.
 *
 *   Only ID fields are compared (never free-text names) so historical
 *   snapshots with only names are ignored.
 */
export function detectConflicts(current, linked, keys = ["customer_id", "brand_id", "product_id"]) {
  if (!current || !linked || !linked.found) return [];
  const conflicts = [];
  for (const k of keys) {
    const c = current[k];
    const e = linked[k];
    if (!c || !e) continue;
    if (c !== e) {
      conflicts.push({ field: k, current: c, expected: e });
    }
  }
  return conflicts;
}
